import React, { useState } from "react";
import { IntlProvider } from "react-intl";

import Albania from "../Language/alb.json";
import English from "../Language/en.json";

export const SUPPORTED_LOCALES = ["en", "alb"] as const;
export type AppLang = (typeof SUPPORTED_LOCALES)[number];

export const Context = React.createContext({
  locale: "alb" as AppLang,
  selectLanguage: (_value: string) => {},
});

/** Normalize browser / stored values to app locale codes (`en` | `alb`). */
export const resolveAppLocale = (raw: string | null | undefined): AppLang => {
  if (!raw) return "alb";
  const value = raw.toLowerCase().trim();
  if (value === "en" || value.startsWith("en-") || value.startsWith("en_")) {
    return "en";
  }
  // Albanian: sq, sq-AL, alb (legacy app code)
  if (
    value === "alb" ||
    value === "sq" ||
    value.startsWith("sq-") ||
    value.startsWith("sq_")
  ) {
    return "alb";
  }
  // Kosovo / regional product: prefer Albanian when language is unknown
  return "alb";
};

const detectInitialLocale = (): AppLang => {
  const stored = localStorage.getItem("lang");
  if (stored === "en" || stored === "alb") {
    return stored;
  }
  const browser =
    (typeof navigator !== "undefined" &&
      (navigator.languages?.[0] || navigator.language)) ||
    "";
  // Explicit English browser → English; otherwise Albanian (Kosovo market)
  if (browser.toLowerCase().startsWith("en")) {
    return "en";
  }
  return resolveAppLocale(browser || "alb");
};

const MESSAGES: Record<AppLang, typeof English> = {
  en: English,
  alb: Albania,
};

const initialLocale = detectInitialLocale();
if (localStorage.getItem("lang") !== initialLocale) {
  localStorage.setItem("lang", initialLocale);
}

const LanguageWrapper = (props: any) => {
  const [locale, setLocale] = useState<AppLang>(initialLocale);
  const [messages, setMessages] = useState(MESSAGES[initialLocale]);

  function selectLanguage(value: string) {
    const newLocale = resolveAppLocale(value);
    localStorage.setItem("lang", newLocale);
    setLocale(newLocale);
    setMessages(MESSAGES[newLocale]);
    // Reload so Yup schemas / local_language reads pick up the new lang
    window.location.reload();
  }

  // "alb" is our message-file key; Intl APIs need BCP 47 "sq" (Albanian).
  const intlLocale = locale === "alb" ? "sq" : "en";

  return (
    <Context.Provider value={{ locale, selectLanguage }}>
      <IntlProvider
        messages={messages}
        locale={intlLocale}
        defaultLocale="en"
        onError={(err) => {
          // Browser may lack full formatjs locale packs for "sq"; messages still load from alb.json.
          if (err.code === "MISSING_DATA") return;
          console.error(err);
        }}
      >
        {props.children}
      </IntlProvider>
    </Context.Provider>
  );
};

export default LanguageWrapper;
